import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type { 
  PayerWizardProgress, 
  PlanConfig, 
  PlanLaneConfig,
  RuleReviewSummary,
  StepValidation,
} from '@/lib/payer-wizard/types';
import { createInitialWizardState, createDefaultPlanLaneConfig, validateLaneConfig } from '@/lib/payer-wizard/types';
import type { PayerType, RuleSet, Rule, RuleConfidence } from '@/lib/rules/rulesetTypes';
import { logAudit } from '@/lib/security/auditLogger';

export function usePayerWizard() {
  const [progress, setProgress] = useState<PayerWizardProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ruleReview, setRuleReview] = useState<Record<string, RuleReviewSummary>>({});

  // Initialize a new wizard session
  const initializeWizard = useCallback(() => {
    const now = new Date().toISOString();
    const initial = createInitialWizardState();
    setProgress({
      ...initial,
      id: crypto.randomUUID(),
      created_at: now,
      updated_at: now,
      created_by: null,
    });
    setError(null);
  }, []);

  // Validate Step 1 - Payer Setup (simplified - no lane priority here)
  const validateStep1 = useCallback((): StepValidation => {
    if (!progress) return { isValid: false, errors: ['Wizard not initialized'] };
    
    const errors: string[] = [];
    if (!progress.payer_name.trim()) errors.push('Payer name is required');
    if (!progress.payer_type) errors.push('Payer type is required');
    if (!progress.jurisdiction) errors.push('Jurisdiction is required');
    
    return { isValid: errors.length === 0, errors };
  }, [progress]);

  // Validate Step 2 - Plan Setup
  const validateStep2 = useCallback((): StepValidation => {
    if (!progress) return { isValid: false, errors: ['Wizard not initialized'] };
    
    const errors: string[] = [];
    if (progress.plans.length === 0) errors.push('At least one plan is required');
    
    const hasDefault = progress.plans.some(p => p.is_default);
    if (!hasDefault && progress.plans.length > 0) {
      errors.push('One plan must be marked as default');
    }
    
    for (const plan of progress.plans) {
      if (!plan.plan_name.trim()) errors.push('All plans must have a name');
      if (!plan.ruleset_source) errors.push('All plans must have a ruleset source');
      if (plan.ruleset_source === 'EXISTING_PLAN' && !plan.source_id) {
        errors.push('Source plan must be selected when cloning from existing');
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }, [progress]);

  // Validate Step 3 - Rule Review
  const validateStep3 = useCallback((): StepValidation => {
    if (!progress) return { isValid: false, errors: ['Wizard not initialized'] };
    
    const errors: string[] = [];
    if (!progress.rules_reviewed) {
      errors.push('You must review and confirm the rule configuration');
    }
    
    return { isValid: errors.length === 0, errors };
  }, [progress]);

  // Validate Step 4 - Lane Configuration (per-plan)
  const validateStep4 = useCallback((): StepValidation => {
    if (!progress) return { isValid: false, errors: ['Wizard not initialized'] };
    
    const errors: string[] = [];
    
    // Every plan must have at least one lane enabled
    for (let i = 0; i < progress.plans.length; i++) {
      const laneConfig = progress.plan_lane_configs[i];
      if (!laneConfig) {
        errors.push(`Plan "${progress.plans[i].plan_name}" has no lane configuration`);
        continue;
      }
      
      const validation = validateLaneConfig(laneConfig);
      if (!validation.valid) {
        errors.push(`Plan "${progress.plans[i].plan_name}": ${validation.error}`);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  }, [progress]);

  // Update Step 1 data
  const updatePayerSetup = useCallback((data: {
    payer_name?: string;
    payer_type?: PayerType;
    jurisdiction?: string;
    portal_url?: string | null;
    phone?: string | null;
    fax?: string | null;
  }) => {
    setProgress(prev => prev ? {
      ...prev,
      ...data,
      updated_at: new Date().toISOString(),
    } : null);
  }, []);

  // Update Step 2 - Plans (also sync lane configs)
  const updatePlans = useCallback((plans: PlanConfig[]) => {
    setProgress(prev => {
      if (!prev) return null;
      
      // Create lane configs for new plans, keep existing for unchanged
      const newLaneConfigs: PlanLaneConfig[] = plans.map((_, idx) => {
        return prev.plan_lane_configs[idx] || createDefaultPlanLaneConfig(idx);
      });
      
      return {
        ...prev,
        plans,
        plan_lane_configs: newLaneConfigs,
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Add a plan
  const addPlan = useCallback((plan: PlanConfig) => {
    setProgress(prev => {
      if (!prev) return null;
      const newIndex = prev.plans.length;
      return {
        ...prev,
        plans: [...prev.plans, plan],
        plan_lane_configs: [...prev.plan_lane_configs, createDefaultPlanLaneConfig(newIndex)],
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Remove a plan
  const removePlan = useCallback((index: number) => {
    setProgress(prev => {
      if (!prev) return null;
      return {
        ...prev,
        plans: prev.plans.filter((_, i) => i !== index),
        plan_lane_configs: prev.plan_lane_configs.filter((_, i) => i !== index),
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Update lane configuration for a specific plan
  const updatePlanLaneConfig = useCallback((planIndex: number, config: PlanLaneConfig) => {
    setProgress(prev => {
      if (!prev) return null;
      const newConfigs = [...prev.plan_lane_configs];
      newConfigs[planIndex] = config;
      return {
        ...prev,
        plan_lane_configs: newConfigs,
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Mark rules as reviewed
  const confirmRulesReviewed = useCallback(() => {
    setProgress(prev => prev ? {
      ...prev,
      rules_reviewed: true,
      updated_at: new Date().toISOString(),
    } : null);
  }, []);

  // Complete a step
  const completeStep = useCallback((step: number) => {
    setProgress(prev => {
      if (!prev) return null;
      const key = `step${step}_complete` as keyof PayerWizardProgress;
      return {
        ...prev,
        [key]: true,
        current_step: Math.max(prev.current_step, step + 1),
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Go to a specific step
  const goToStep = useCallback((step: number) => {
    setProgress(prev => prev ? {
      ...prev,
      current_step: step,
      updated_at: new Date().toISOString(),
    } : null);
  }, []);

  // Fetch template ruleset for preview
  const fetchTemplateRules = useCallback(async (templateName: string): Promise<RuleReviewSummary | null> => {
    try {
      if (ruleReview[templateName]) {
        return ruleReview[templateName];
      }

      let summary: RuleReviewSummary;
      
      if (templateName === 'BWC_OH_BASE_2026') {
        summary = {
          auth_requirement_default: 'CONDITIONAL',
          doc_requirements: ['H&P', 'Imaging', 'Conservative Treatment History', 'C9 Form'],
          sla_hours: 336,
          follow_up_cadence_hours: 48,
          rule_count: 25,
        };
      } else {
        summary = {
          auth_requirement_default: 'CONDITIONAL',
          doc_requirements: ['H&P', 'Imaging', 'LOB'],
          sla_hours: 336,
          follow_up_cadence_hours: 72,
          rule_count: 18,
        };
      }

      setRuleReview(prev => ({ ...prev, [templateName]: summary }));
      return summary;
    } catch (err) {
      console.error('Failed to fetch template rules:', err);
      return null;
    }
  }, [ruleReview]);

  // Fetch existing rulesets for cloning
  const fetchExistingRulesets = useCallback(async (): Promise<RuleSet[]> => {
    try {
      const { data, error: queryError } = await supabase
        .from('rulesets')
        .select('*, payer:payers(name), plan:plans(plan_name)')
        .eq('status', 'ACTIVE')
        .order('updated_at', { ascending: false })
        .limit(50);
      
      if (queryError) throw queryError;
      return (data || []) as unknown as RuleSet[];
    } catch (err) {
      console.error('Failed to fetch existing rulesets:', err);
      return [];
    }
  }, []);

  // Create payer in database
  const createPayer = useCallback(async (): Promise<string | null> => {
    if (!progress) return null;
    
    try {
      // Collect all enabled lane types from all plans for payer-level summary
      const allLanes = new Set<string>();
      progress.plan_lane_configs.forEach(config => {
        config.lane_priority.forEach(lane => allLanes.add(lane));
      });
      
      const hasPasEnabled = progress.plan_lane_configs.some(c => c.lanes_enabled.FHIR_PAS);
      
      const { data, error: insertError } = await supabase
        .from('payers')
        .insert({
          payer_id: progress.payer_name.toLowerCase().replace(/\s+/g, '_'),
          name: progress.payer_name,
          payer_type: progress.payer_type,
          submission_lane_priority: Array.from(allLanes),
          portal_url: progress.portal_url,
          phone: progress.phone,
          fax: progress.fax,
          epa_enabled: hasPasEnabled,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      setProgress(prev => prev ? {
        ...prev,
        created_payer_id: data.id,
        updated_at: new Date().toISOString(),
      } : null);
      
      return data.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payer');
      return null;
    }
  }, [progress]);

  // Create plan with cloned ruleset
  const createPlanWithRuleset = useCallback(async (
    payerId: string,
    planConfig: PlanConfig,
    laneConfig: PlanLaneConfig
  ): Promise<{ planId: string; rulesetId: string } | null> => {
    if (!progress) return null;
    
    try {
      // 1. Create the plan with lane config
      const { data: planData, error: planError } = await supabase
        .from('plans')
        .insert({
          payer_id: payerId,
          plan_name: planConfig.plan_name,
          state: progress.jurisdiction,
          is_default_plan: planConfig.is_default,
          submission_lane_priority: laneConfig.lane_priority,
          portal_url: laneConfig.portal_url || progress.portal_url,
          phone: laneConfig.phone || progress.phone,
          fax: laneConfig.fax || progress.fax,
        })
        .select()
        .single();
      
      if (planError) throw planError;
      
      // 2. Create the ruleset
      const rulesetName = `${progress.payer_name} - ${planConfig.plan_name}`;
      const { data: rulesetData, error: rulesetError } = await supabase
        .from('rulesets')
        .insert({
          payer_id: payerId,
          plan_id: planData.id,
          jurisdiction: progress.jurisdiction,
          effective_start_date: new Date().toISOString().split('T')[0],
          version: 1,
          status: 'ACTIVE',
          name: rulesetName,
          description: `Cloned from ${planConfig.ruleset_source}`,
        })
        .select()
        .single();
      
      if (rulesetError) throw rulesetError;
      
      // 3. Clone rules
      await cloneRulesFromTemplate(rulesetData.id, planConfig.ruleset_source, planConfig.source_id);
      
      return { planId: planData.id, rulesetId: rulesetData.id };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create plan');
      return null;
    }
  }, [progress]);

  // Clone rules from template
  const cloneRulesFromTemplate = useCallback(async (
    targetRulesetId: string,
    source: string,
    sourceId: string | null
  ): Promise<void> => {
    try {
      let sourceRules: Rule[] = [];
      
      if (source === 'EXISTING_PLAN' && sourceId) {
        const { data } = await supabase
          .from('rules')
          .select('*')
          .eq('ruleset_id', sourceId);
        
        sourceRules = (data || []) as unknown as Rule[];
      } else {
        sourceRules = generateTemplateRules(source);
      }
      
      if (sourceRules.length > 0) {
        const clonedRules = sourceRules.map(rule => ({
          ruleset_id: targetRulesetId,
          rule_type: rule.rule_type,
          match_criteria: rule.match_criteria as Json,
          decision: rule.decision as Json,
          priority: rule.priority,
          confidence: 'TEMPLATE' as RuleConfidence,
          is_active: true,
          notes: `Cloned from ${source}`,
        }));
        
        await supabase.from('rules').insert(clonedRules);
      }
    } catch (err) {
      console.error('Failed to clone rules:', err);
    }
  }, []);

  // Generate template rules
  const generateTemplateRules = (templateName: string): Rule[] => {
    const now = new Date().toISOString();
    const baseRules: Partial<Rule>[] = [];
    
    if (templateName === 'BWC_OH_BASE_2026') {
      baseRules.push(
        {
          rule_type: 'AUTH_REQUIREMENT',
          match_criteria: { cpt_range: { start: '27000', end: '27899' } },
          decision: { auth_required: 'CONDITIONAL', condition_description: 'Auth required for elective orthopedic procedures' },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'DOC_REQUIREMENT',
          match_criteria: {},
          decision: { required_documents: ['H&P', 'Imaging', 'C9 Form', 'Conservative Treatment History'] },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'SLA_TIMER',
          match_criteria: { urgency: 'routine' },
          decision: { sla_hours: 336, urgent_sla_hours: 72 },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'FOLLOW_UP_CADENCE',
          match_criteria: {},
          decision: { initial_follow_up_hours: 48, follow_up_cadence_hours: 48 },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'SUBMISSION_LANE',
          match_criteria: {},
          decision: { primary_lane: 'PORTAL', fallback_lane: 'FAX_PHONE' },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        }
      );
    } else {
      baseRules.push(
        {
          rule_type: 'AUTH_REQUIREMENT',
          match_criteria: { cpt_range: { start: '27000', end: '27899' } },
          decision: { auth_required: 'CONDITIONAL', condition_description: 'Auth typically required for surgical procedures' },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'DOC_REQUIREMENT',
          match_criteria: {},
          decision: { required_documents: ['H&P', 'Imaging', 'Letter of Medical Necessity'] },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'SLA_TIMER',
          match_criteria: { urgency: 'routine' },
          decision: { sla_hours: 336, urgent_sla_hours: 72 },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'FOLLOW_UP_CADENCE',
          match_criteria: {},
          decision: { initial_follow_up_hours: 72, follow_up_cadence_hours: 72 },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        },
        {
          rule_type: 'SUBMISSION_LANE',
          match_criteria: {},
          decision: { primary_lane: 'PORTAL', fallback_lane: 'FAX_PHONE' },
          priority: 100,
          confidence: 'TEMPLATE',
          is_active: true,
        }
      );
    }
    
    return baseRules.map((rule, idx) => ({
      ...rule,
      id: crypto.randomUUID(),
      ruleset_id: '',
      created_at: now,
      last_updated_at: now,
    })) as Rule[];
  };

  // Activate payer and all plans
  const activatePayer = useCallback(async (): Promise<boolean> => {
    if (!progress) return false;
    
    setLoading(true);
    setError(null);
    
    try {
      // 1. Create the payer
      const payerId = await createPayer();
      if (!payerId) throw new Error('Failed to create payer');
      
      // 2. Create all plans with rulesets and lane configs
      const planIds: string[] = [];
      const rulesetIds: string[] = [];
      
      for (let i = 0; i < progress.plans.length; i++) {
        const planConfig = progress.plans[i];
        const laneConfig = progress.plan_lane_configs[i];
        
        const result = await createPlanWithRuleset(payerId, planConfig, laneConfig);
        if (result) {
          planIds.push(result.planId);
          rulesetIds.push(result.rulesetId);
        }
      }
      
      // 3. Log activation audit event
      await logAudit({
        action: 'config_changed',
        resourceType: 'payer_configuration',
        resourceId: payerId,
        details: {
          payer_name: progress.payer_name,
          payer_type: progress.payer_type,
          jurisdiction: progress.jurisdiction,
          plans_created: planIds.length,
          rulesets_created: rulesetIds.length,
          plan_lane_configs: progress.plan_lane_configs,
        },
        phiAccessed: false,
      });
      
      // 4. Update progress with created IDs
      setProgress(prev => prev ? {
        ...prev,
        created_payer_id: payerId,
        created_plan_ids: planIds,
        created_ruleset_ids: rulesetIds,
        step5_complete: true,
        updated_at: new Date().toISOString(),
      } : null);
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate payer');
      return false;
    } finally {
      setLoading(false);
    }
  }, [progress, createPayer, createPlanWithRuleset]);

  // Check if activation is allowed
  const canActivate = useCallback((): { allowed: boolean; reasons: string[] } => {
    if (!progress) return { allowed: false, reasons: ['Wizard not initialized'] };
    
    const reasons: string[] = [];
    
    if (!progress.step1_complete) reasons.push('Payer setup not complete');
    if (!progress.step2_complete) reasons.push('Plan setup not complete');
    if (!progress.step3_complete) reasons.push('Rule review not complete');
    if (!progress.step4_complete) reasons.push('Lane configuration not complete');
    
    // Safety checks
    if (progress.plans.length === 0) {
      reasons.push('At least one plan is required');
    }
    
    // Check each plan has at least one lane
    for (let i = 0; i < progress.plans.length; i++) {
      const laneConfig = progress.plan_lane_configs[i];
      if (!laneConfig) {
        reasons.push(`Plan "${progress.plans[i].plan_name}" has no lane configuration`);
      } else {
        const validation = validateLaneConfig(laneConfig);
        if (!validation.valid) {
          reasons.push(`Plan "${progress.plans[i].plan_name}": ${validation.error}`);
        }
      }
    }
    
    return { allowed: reasons.length === 0, reasons };
  }, [progress]);

  return {
    progress,
    loading,
    error,
    ruleReview,
    initializeWizard,
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    updatePayerSetup,
    updatePlans,
    addPlan,
    removePlan,
    updatePlanLaneConfig,
    confirmRulesReviewed,
    completeStep,
    goToStep,
    fetchTemplateRules,
    fetchExistingRulesets,
    activatePayer,
    canActivate,
  };
}
